// src/hooks/useTaskBoard.js
/**
 * useTaskBoard — primary hook for board-level logic.
 *
 * Encapsulates the drag-and-drop state management so KanbanBoard
 * stays declarative and free of event-handling boilerplate.
 */
import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { moveTask } from '../store/slices/tasksSlice.js';

export function useTaskBoard() {
  const dispatch = useDispatch();
  const [dragOverCol, setDragOverCol] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const handleDragStart = useCallback((taskId) => {
    setDraggingId(taskId);
  }, []);

  const handleDragOver = useCallback((status) => {
    setDragOverCol(status);
  }, []);

  const handleDrop = useCallback(
    (e, status) => {
      e.preventDefault();
      const taskId = e.dataTransfer.getData('taskId');
      if (taskId) dispatch(moveTask({ taskId, status }));
      setDragOverCol(null);
      setDraggingId(null);
    },
    [dispatch]
  );

  const handleDragLeave = useCallback((e) => {
    // Only clear when leaving the column entirely (not entering a child element)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverCol(null);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragOverCol(null);
    setDraggingId(null);
  }, []);

  return {
    dragOverCol,
    draggingId,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragLeave,
    handleDragEnd,
  };
}
